/**
 * The `sites` Worker's KV record (docs/53 §7.6 step 4): written by the publish Workflow (`jobs`) when a version goes
 * live or rolls back, read by `sites` on every cache miss. One read answers host → tenant → live version.
 *
 *   KV HOSTMAP  host:<hostname>  →  HostRecord
 *   R2 VERSIONS t/<tenantId>/v<N>/<path>   (the rendered, immutable version)
 */
import { z } from 'zod';
import { SiteId, TenantId } from './ids.ts';
import { ReconPage } from './recon.ts';

export const Hostname = z
  .string()
  .max(253)
  .regex(/^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)*[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/, 'expected a lowercase hostname');

export const HostRecord = z.strictObject({
  v: z.literal(1),
  tenantId: TenantId,
  siteId: SiteId,
  /** The live pointer. Null until the first version is ready: the host answers 503 "no live version yet". */
  liveVersion: z.number().int().min(1).nullable(),
  canonicalHost: Hostname,
  /**
   * `prospect` hosts need a signed preview token; `moved` serves a neutral page (docs/53 T-19); `paused` is a site
   * whose service the failed payment ladder paused on day 14 (c014): every page answers 503 with Retry-After and a
   * plain "temporarily unavailable" page, robots.txt still answers, and paying again writes `live` straight back.
   */
  status: z.enum(['prospect', 'live', 'moved', 'paused']),
  /** The business name for the paused page. Optional: records written before it existed show no name. */
  siteName: z.string().min(1).max(80).optional(),
  updatedAt: z.iso.datetime({ offset: false }),
  /**
   * When the site went live (`tenants.go_live_at`), for the HSTS ramp on client domains (handbook 11.6: one day of
   * max-age for the first 30 days, then a year). Optional and nullable: records written before it existed, and sites
   * not live yet, get the short max-age.
   */
  liveSince: z.iso.datetime({ offset: false }).nullable().optional(),
});
export type HostRecord = z.infer<typeof HostRecord>;

export const hostKey = (hostname: string) => `host:${hostname.toLowerCase()}`;

/** R2 key prefix of one version: `t/<tenantId>/v<N>/`. */
export function versionKeyPrefix(tenantId: string, version: number): string {
  TenantId.parse(tenantId);
  if (!Number.isInteger(version) || version < 1) throw new Error('version must be a positive integer');
  return `t/${tenantId}/v${version}/`;
}

/** First-party events a site may send to `/_e` (docs/20 §12.3 plus `page_view`, and `project_view` for a project page, R6-18). */
export const SITE_EVENTS = [
  'page_view',
  'call_click',
  'form_start',
  'form_submit',
  'form_error',
  'estimator_step',
  'estimator_complete',
  'booking_open',
  'directions_click',
  'email_click',
  'cta_click',
  'review_link_click',
  'offer_view',
  'project_view',
  'js_error',
  'web_vitals',
] as const;
export const SiteEvent = z.enum(SITE_EVENTS);
export type SiteEvent = z.infer<typeof SiteEvent>;

/**
 * The events the nightly roll up keeps per day in `event_daily`, under the same names the site sends. One vocabulary
 * (measurement spec §6 gap 2): before migration 0010 the table said `call_tap`, `direction_tap` and `booking_click`
 * where the site says `call_click`, `directions_click` and `booking_open`. `js_error` and `web_vitals` are for us and
 * aren't counted per day. This list is the table's CHECK list (DB_ENUMS), so the two can't drift.
 */
export const EVENT_METRICS = [
  'page_view',
  'call_click',
  'form_start',
  'form_submit',
  'form_error',
  'estimator_step',
  'estimator_complete',
  'booking_open',
  'directions_click',
  'email_click',
  'cta_click',
  'review_link_click',
  'offer_view',
  'project_view',
] as const satisfies readonly (typeof SITE_EVENTS)[number][];
export type EventMetricName = (typeof EVENT_METRICS)[number];

/** The names `event_daily` used before migration 0010, for anything still holding an old row or an old key. */
export const LEGACY_EVENT_METRIC: Readonly<Record<string, EventMetricName>> = { call_tap: 'call_click', direction_tap: 'directions_click', booking_click: 'booking_open' };

/** A site event's daily metric, or null when it isn't counted per day. */
export function eventMetricOf(event: string): EventMetricName | null {
  const name = LEGACY_EVENT_METRIC[event] ?? event;
  return (EVENT_METRICS as readonly string[]).includes(name) ? (name as EventMetricName) : null;
}

/**
 * The prospect page record (docs/53 §6.2 `/p/:slug`, docs/12): written to KV by the console when it publishes a
 * prospect page from `engine`, read by `app` on every view. One KV read, like HostRecord, so `core` never reads
 * `engine` at request time (docs/53 §2.4). Added by WP-API.
 *
 *   KV PROSPECTS  prospect:<slug>  →  ProspectRecord
 */
export const ProspectSlug = z.string().regex(/^[a-z0-9]{16,40}$/);

export const ProspectRecord = z.strictObject({
  v: z.literal(1),
  slug: ProspectSlug,
  /** The prospect tenant in `core` (status `prospect`) that checkout promotes. */
  tenantId: TenantId,
  businessName: z.string().min(2).max(60),
  /**
   * A signed preview URL (`?pt=`), minted with @ff/sites/preview-token when the page is published. Null on a reply
   * recon page (R6-C6), which shows no built site: a preview is never promised (c005) and c051 is still open.
   */
  previewUrl: z.url({ protocol: /^https?$/ }).max(2048).nullable(),
  expiresAt: z.iso.datetime({ offset: false }),
  map: z.object({ keyword: z.string().max(120), top3Points: z.number().int().min(0).max(81), pointsPath: z.string().max(512) }).nullable(),
  /** The eligibility gate (docs/41 §7.2): false shows the page without a Start button. */
  eligible: z.boolean(),
  status: z.enum(['live', 'withdrawn', 'converted']),
  updatedAt: z.iso.datetime({ offset: false }),
  /** The reply recon (R6-C6, docs/12 §9): when present, the page is the recon in the prospecting plan §5 order. */
  recon: ReconPage.optional(),
});
export type ProspectRecord = z.infer<typeof ProspectRecord>;

export const prospectKey = (slug: string) => `prospect:${slug}`;
